import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
// GapPanel вернётся вместе с вкладкой «Вариации».
import { GapRow } from '../../ui/Gaps'
import { useUnsavedWarning } from '../../ui/useUnsavedWarning'
import { Icon } from '../../ui/Icon'
import { DetailHead } from '../../ui/DetailHead'
import { Loading, failureText } from '../../ui/Load'
import { Select } from '../../ui/Select'
import { useSession } from '../auth/session'
import { useCategories, type Category } from '../categories/api'
import { formatAmount, formatDayTime, withCountRu } from '@dorixona/utils'
import { useProduct, useProductStock, useSaveProduct, type Product, type ProductTexts } from './api'
import { priceRangeLabel, stockSummary } from './model'
import { ProductPhotos } from './ProductPhotos'
import { SalesTab } from './ProductSales'
import { StockTab } from './ProductStock'
import { ProductTextsEditor } from './ProductTextsEditor'
import { listAddress } from '../../app/listMemory'

export interface ProductScreenProps {
  onToast: (message: string) => void
}

type Tab = 'main' | 'vars' | 'stock' | 'sales'

/** Всё, что во вкладке «Карточка» сохраняется одной кнопкой. */
interface Draft {
  categoryId: number
  /** Пустая строка — «как у категории»: у своего процента есть состояние «не задан». */
  cbOwn: string
  rx: boolean
  popular: boolean
  ru: ProductTexts
  uz: ProductTexts
}

function toDraft(product: Product): Draft {
  return {
    categoryId: product.categoryId,
    cbOwn: product.cbOwn === null ? '' : String(product.cbOwn),
    rx: product.rx,
    popular: product.popular,
    ru: product.ru,
    uz: product.uz,
  }
}

/** Свой процент: пусто — берём категорийный, иначе целое от 0 до 30. */
export function ownCashback(value: string): number | null {
  if (value.trim() === '') return null
  return Math.max(0, Math.min(30, Math.round(Number(value) || 0)))
}

/** Есть ли в черновике то, чего нет на сервере. */
function changed(draft: Draft, product: Product): boolean {
  const saved = toDraft(product)
  return (
    draft.categoryId !== saved.categoryId ||
    ownCashback(draft.cbOwn) !== product.cbOwn ||
    draft.rx !== saved.rx ||
    draft.popular !== saved.popular ||
    JSON.stringify([draft.ru, draft.uz]) !== JSON.stringify([saved.ru, saved.uz])
  )
}

/** Подсказка под своим процентом: чем обернётся пустое поле. */
function cashbackHint(category: Category | undefined): string {
  return category ? `Пусто — как у категории: ${category.cb}%` : 'Пусто — процент категории.'
}

const TABS: [Tab, string][] = [
  ['main', 'Карточка'],
  // Вариаций у товаров пока нет — вкладка скрыта, пока они не появятся в API (F-25).
  // ['vars', 'Вариации'],
  ['stock', 'Остатки'],
  ['sales', 'Продажи'],
]

/**
 * Карточка товара. Слева то, чем владеет FOM и что здесь только читается,
 * и тексты витрины; справа — настройки витрины и фото (R-15).
 *
 * Правки вкладки «Карточка» копятся в черновике и уходят одной кнопкой
 * «Сохранить» одним запросом. Черновик хранит только правки: товар с сервера
 * меняется и без них (переключатель видимости, новое фото), и эти ответы не
 * должны стирать то, что человек успел напечатать.
 */
export function ProductScreen({ onToast }: ProductScreenProps) {
  const { can } = useSession()
  const navigate = useNavigate()
  const params = useParams()
  const [tab, setTab] = useState<Tab>('main')
  const id = Number(params.id)

  const product = useProduct(id)
  // Остатки нужны уже в шапке: цена по сети — вилкой, итог — в штуках.
  const stock = useProductStock(id, true)
  const summary = stock.data ? stockSummary(stock.data) : null
  const categories = useCategories()
  const save = useSaveProduct()
  // null — правок нет, форма показывает то, что на сервере.
  const [edits, setEdits] = useState<Draft | null>(null)

  // Другой товар — другой черновик.
  useEffect(() => {
    setEdits(null)
  }, [id])

  // Черновик появляется только у загруженного товара — товар тогда уже есть.
  const dirty = edits !== null && changed(edits, product.data as Product)

  // Уход со страницы с несохранёнными правками браузер переспросит.
  useUnsavedWarning(dirty)

  if (product.isPending) return <Loading what="товар" />
  if (product.isError) {
    return (
      <div className="ui-card">
        <div className="ui-empty">
          <Icon name="i-pill" />
          <b>Товар не найден</b>
          <p>Возможно, его убрали из каталога в FOM.</p>
          <button
            type="button"
            className="ui-btn"
            data-variant="secondary"
            onClick={() => {
              void navigate(listAddress('/products'))
            }}
          >
            К списку товаров
          </button>
        </div>
      </div>
    )
  }

  const item = product.data
  const draft = edits ?? toDraft(item)
  const edit = (patch: Partial<Draft>) => {
    setEdits({ ...draft, ...patch })
  }

  const canUpdate = can('products', 'u')
  const sorted = [...(categories.data ?? [])].sort((a, b) => a.sort - b.sort)
  // Процент в подсказке — той категории, что выбрана сейчас, а не сохранённой.
  const picked = sorted.find((c) => c.id === draft.categoryId)

  function toggleActive(active: boolean) {
    save.mutate(
      { id, patch: { active } },
      {
        onSuccess: () => {
          onToast(active ? 'Товар показан в приложении' : 'Товар скрыт из приложения')
        },
        onError: (error) => {
          onToast(failureText(error))
        },
      },
    )
  }

  function saveAll() {
    save.mutate(
      {
        id,
        patch: {
          categoryId: draft.categoryId || null,
          cbOwn: ownCashback(draft.cbOwn),
          rx: draft.rx,
          popular: draft.popular,
          ru: draft.ru,
          uz: draft.uz,
        },
      },
      {
        onSuccess: () => {
          setEdits(null)
          onToast('Изменения сохранены — обновятся в приложении')
        },
        onError: (error) => {
          onToast(failureText(error))
        },
      },
    )
  }

  return (
    <>
      <DetailHead
        backLabel="Товары"
        onBack={() => {
          void navigate(listAddress('/products'))
        }}
        title={item.name}
        sub={
          <>
            {item.categoryName ? (
              <span className="ui-tag">{item.categoryName}</span>
            ) : (
              <span className="ui-tag" data-tone="warning">
                без категории
              </span>
            )}
            {item.rx ? (
              <span className="ui-tag" data-tone="rx">
                по рецепту
              </span>
            ) : null}
            {item.popular ? <span className="ui-tag">хит продаж</span> : null}
            <span className="dot" aria-hidden="true" />
            <span className="pd-price">{priceRangeLabel(item, summary)}</span>
            <span className="dot" aria-hidden="true" />
            <span>
              кэшбэк <b className="pd-cb">{item.cb}%</b>
              {item.cbOwn === null ? ' от категории' : ' свой'}
            </span>
            <span className="ui-status" data-tone={item.inStock ? 'success' : 'neutral'}>
              {item.inStock ? 'есть в наличии' : 'нет в наличии'}
            </span>
            {summary ? <span className="num">остаток {formatAmount(summary.qty)} шт</span> : null}
          </>
        }
        actions={
          canUpdate ? (
            <label className="ui-switch pd-active">
              <input
                type="checkbox"
                role="switch"
                checked={item.active}
                onChange={(e) => {
                  toggleActive(e.target.checked)
                }}
              />
              Показывать в приложении
            </label>
          ) : (
            <>
              <span className="ui-status" data-tone={item.active ? 'success' : 'neutral'}>
                {item.active ? 'в приложении' : 'скрыт'}
              </span>
              <span className="ui-tag">только просмотр</span>
            </>
          )
        }
      />

      <div className="ui-tabs ui-detail-tabs" role="tablist" aria-label="Разделы карточки">
        {TABS.map(([key, name]) => (
          <button
            key={key}
            type="button"
            role="tab"
            className="ui-tab"
            aria-selected={tab === key}
            onClick={() => {
              setTab(key)
            }}
          >
            {name}
            {/* Правки не пропадают при переходе на другую вкладку — точка
                напоминает, что они ждут сохранения. */}
            {key === 'main' && dirty ? (
              <span className="ui-dirty-dot" aria-label="есть несохранённые изменения" />
            ) : null}
          </button>
        ))}
      </div>

      {tab === 'main' ? (
        <>
          <div className="ui-detail-grid">
            <div className="ui-detail-col">
              <section className="ui-card" aria-labelledby="pd-fom">
                <div className="ui-card-head">
                  <h3 id="pd-fom">
                    Данные из FOM <span className="ui-tag">FOM</span>
                  </h3>
                  <span className="aside">
                    <Icon name="i-key" />
                    меняются только в FOM
                  </span>
                </div>
                <div className="ui-card-body pd-facts-body">
                  <div className="ui-facts">
                    <div>
                      <span className="k">Торговое название</span>
                      <span className="v">{item.name}</span>
                    </div>
                    <div>
                      <span className="k">Код в FOM</span>
                      <span className="v">
                        {item.fomCode ? <span className="ui-code">{item.fomCode}</span> : '—'}
                      </span>
                    </div>
                    <div>
                      {/* FOM присылает производителя и страну одной строкой (поле R). */}
                      <span className="k">Производитель · страна</span>
                      <span className="v">{item.maker || '—'}</span>
                    </div>
                    <GapRow
                      what="Действующее вещество и форма выпуска"
                      where="FOM их не передаёт: в файле остатков только I, N, R, K, P, O"
                    />
                    <div>
                      <span className="k">Цена</span>
                      <span className="v num">{priceRangeLabel(item, summary)}</span>
                    </div>
                    <div>
                      <span className="k">Остаток по сети</span>
                      <span className="v num">
                        {summary
                          ? `${formatAmount(summary.qty)} шт · ${withCountRu(summary.stores, ['аптека', 'аптеки', 'аптек'])}`
                          : item.inStock
                            ? 'есть'
                            : 'нет'}
                      </span>
                    </div>
                    <div>
                      <span className="k">Последняя синхронизация</span>
                      <span className="v num">
                        {item.syncedAt ? formatDayTime(item.syncedAt) : 'ещё не было'}
                      </span>
                    </div>
                  </div>
                </div>
              </section>

              <ProductTextsEditor
                ru={draft.ru}
                uz={draft.uz}
                disabled={!canUpdate}
                onChange={(lang, key, value) => {
                  edit({ [lang]: { ...draft[lang], [key]: value } })
                }}
              />
            </div>

            <div className="ui-detail-col">
              <section className="ui-card" aria-labelledby="pd-showcase">
                <div className="ui-card-head">
                  <h3 id="pd-showcase">Витрина</h3>
                </div>
                <div className="ui-card-body ui-form">
                  <div className="ui-field">
                    <span className="ui-label" aria-hidden="true">
                      Категория
                    </span>
                    <Select
                      label="Категория"
                      value={draft.categoryId}
                      disabled={!canUpdate}
                      options={[
                        { value: 0, label: 'Без категории' },
                        ...sorted.map((c) => ({ value: c.id, label: c.name })),
                      ]}
                      onChange={(categoryId) => {
                        edit({ categoryId })
                      }}
                    />
                  </div>
                  <div className="ui-field">
                    <label className="ui-label" htmlFor="p-cb">
                      Свой процент кэшбэка
                    </label>
                    <div className="ui-unit">
                      <input
                        id="p-cb"
                        className="ui-input"
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={30}
                        disabled={!canUpdate}
                        placeholder={picked ? `${picked.cb}` : ''}
                        aria-describedby="p-cb-hint"
                        value={draft.cbOwn}
                        onChange={(e) => {
                          edit({ cbOwn: e.target.value })
                        }}
                      />
                      <span aria-hidden="true">%</span>
                    </div>
                    <span className="ui-hint" id="p-cb-hint">
                      {cashbackHint(picked)}
                    </span>
                  </div>
                  <div className="pd-checks">
                    <label className="ui-check">
                      <input
                        type="checkbox"
                        disabled={!canUpdate}
                        checked={draft.rx}
                        aria-describedby={draft.rx ? 'p-rx-hint' : undefined}
                        onChange={(e) => {
                          edit({ rx: e.target.checked })
                        }}
                      />
                      Отпуск по рецепту
                    </label>
                    {draft.rx ? (
                      <span className="ui-hint pd-check-hint" id="p-rx-hint">
                        Не доставляется — только выдача в аптеке
                      </span>
                    ) : null}
                    <label className="ui-check">
                      <input
                        type="checkbox"
                        disabled={!canUpdate}
                        checked={draft.popular}
                        onChange={(e) => {
                          edit({ popular: e.target.checked })
                        }}
                      />
                      На полке «Популярное»
                    </label>
                  </div>
                </div>
              </section>

              <ProductPhotos item={item} canUpdate={canUpdate} onToast={onToast} />
            </div>
          </div>

          {canUpdate ? (
            <div className="ui-savebar" data-dirty={dirty || undefined}>
              <span className="state" role="status">
                {dirty ? 'Есть несохранённые изменения' : 'Все изменения сохранены'}
              </span>
              {dirty ? (
                <button
                  type="button"
                  className="ui-btn"
                  data-variant="secondary"
                  disabled={save.isPending}
                  onClick={() => {
                    setEdits(null)
                  }}
                >
                  Отменить
                </button>
              ) : null}
              <button
                type="button"
                className="ui-btn"
                disabled={!dirty || save.isPending}
                aria-busy={save.isPending}
                onClick={saveAll}
              >
                Сохранить
              </button>
            </div>
          ) : null}
        </>
      ) : null}

      {/* Вкладка «Вариации» скрыта, пока вариации не появятся в API (F-25).
      {tab === 'vars' ? (
        <GapPanel what="Вариации товара" where="GET /admin/products/{id} · F-25">
          Дозировки и фасовки в API не приходят: у товара одна цена и один признак наличия по сети.
          Пока вариации не появятся, товар в каталоге и в заказе — это одна позиция.
        </GapPanel>
      ) : null}
      */}

      {tab === 'stock' ? <StockTab item={item} withStores={can('stores')} /> : null}
      {tab === 'sales' ? <SalesTab id={id} withOrders={can('orders')} /> : null}
    </>
  )
}
